/**
 * QuickLinksPanel — collapsible card showing external portals + related iPCLM modules
 * for each L2 module. Reads from /app/frontend/src/config/moduleLinks.js.
 *
 * Usage:
 *   <QuickLinksPanel moduleKey="diversity" />
 *
 * Self-persists collapsed state in localStorage under `qlp:{moduleKey}:expanded`.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, Link2, ExternalLink, ChevronDown, ChevronUp, ArrowUpRight, PencilLine } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { getModuleLinks } from '@/config/moduleLinks';
import { useRails } from '@/components/layout/SourcingRailedLayout';
import api from '@/lib/api';

export function QuickLinksPanel({ moduleKey, _railManaged }) {
  const data = getModuleLinks(moduleKey);
  const railsCtx = useRails();
  const storageKey = `qlp:${moduleKey}:expanded`;
  const [expanded, setExpanded] = useState(true);
  const [health, setHealth] = useState({});

  const toggle = useCallback(() => {
    setExpanded(prev => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch (e) { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  // Restore expanded state from localStorage
  useEffect(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v !== null) setExpanded(v === '1');
    } catch (e) { /* ignore */ }
  }, [storageKey]);

  // Fetch health for this module's external URLs (only when expanded)
  useEffect(() => {
    if (!expanded || !data?.externals?.length) return;
    const urls = data.externals.map(e => e.url).join(',');
    api.get(`/module-links/health?urls=${encodeURIComponent(urls)}`)
      .then(r => setHealth(r.data.health || {}))
      .catch(() => setHealth({}));
  }, [expanded, data]);

  // iter-143: if a RailedLayout is mounted and this isn't the layout's own
  // rail-managed copy, suppress — the layout already renders the QL into
  // the left rail. Prevents inline duplicates on SCM/SRM pages.
  if (railsCtx && !_railManaged) return null;
  if (!data) return null;
  const { externals = [], internals = [], title, authoring } = data;

  return (
    <Card className="mb-3 border-emerald-100" data-testid={`qlp-${moduleKey}`}>
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none bg-gradient-to-r from-emerald-50/60 via-white to-emerald-50/30"
        onClick={toggle}
      >
        <div className="w-6 h-6 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
          <Link2 className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold text-gray-900 truncate">Quick Links · {title}</h3>
            <Badge variant="outline" className="text-[9px] uppercase tracking-wide bg-emerald-50 border-emerald-200 text-emerald-700">
              {externals.length} external · {internals.length} internal
            </Badge>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          className="p-1 rounded hover:bg-emerald-100 text-gray-500 transition"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          data-testid={`qlp-${moduleKey}-toggle`}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Authoring — "Where does the data on this page come from?" */}
          <div data-testid={`qlp-${moduleKey}-authoring`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1.5 mt-2">
              <PencilLine className="w-3 h-3" /> Where Authoring Happens
            </div>
            {authoring ? (
              <RouterLink
                to={authoring.path || '#'}
                data-testid={`qlp-authoring-${moduleKey}`}
                className="block p-2 rounded-md border border-amber-200 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 transition group"
              >
                <div className="text-[11px] font-semibold text-amber-900 flex items-center gap-1">
                  {authoring.where}
                  <ArrowUpRight className="w-2.5 h-2.5 opacity-60" />
                </div>
                {authoring.action && (
                  <div className="text-[10px] text-amber-800 mt-0.5">
                    <span className="font-medium">Action:</span> {authoring.action}
                  </div>
                )}
                {authoring.note && (
                  <div className="text-[10px] text-slate-600 mt-1 leading-snug">{authoring.note}</div>
                )}
              </RouterLink>
            ) : (
              <div className="p-2 rounded-md border border-slate-200 bg-slate-50 text-[11px] text-slate-600"
                   data-testid={`qlp-authoring-${moduleKey}-default`}>
                <div className="font-medium text-slate-700">Authored on this page</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Records on this screen are created and edited here. Cross-module references are linked from the module list below.</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* External Portals */}
          <div data-testid={`qlp-${moduleKey}-externals`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1.5 mt-2">
              <Globe className="w-3 h-3" /> External Portals &amp; Data Sources
            </div>
            <div className="flex flex-wrap gap-1.5">
              {externals.map((ext, i) => {
                const h = health[ext.url];
                const dot = h?.status === 'green' ? 'bg-emerald-500'
                  : h?.status === 'amber' ? 'bg-amber-500'
                  : h?.status === 'red' ? 'bg-red-500'
                  : 'bg-slate-300';
                const tip = h
                  ? `${h.status.toUpperCase()} · ${h.status_code || h.error || '—'} · ${h.response_time_ms ?? '?'}ms`
                  : 'Health: not yet probed';
                return (
                  <a
                    key={i}
                    href={ext.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`qlp-ext-${moduleKey}-${i}`}
                    title={`${ext.note || ext.url}\n${tip}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-emerald-200 bg-white hover:bg-emerald-50 hover:border-emerald-400 text-emerald-700 transition group"
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${dot} ${h?.status === 'green' ? 'animate-pulse' : ''}`}
                      aria-label={`Health ${h?.status || 'unknown'}`}
                      data-testid={`qlp-health-${moduleKey}-${i}`}
                    />
                    <span className="font-medium">{ext.label}</span>
                    <ExternalLink className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />
                  </a>
                );
              })}
              {externals.length === 0 && <span className="text-xs text-slate-400">No external portals listed.</span>}
            </div>
          </div>
          {/* Internal Modules */}
          <div data-testid={`qlp-${moduleKey}-internals`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1.5 mt-2">
              <ArrowUpRight className="w-3 h-3" /> Related iPCLM Modules
            </div>
            <div className="space-y-1">
              {internals.map((m, i) => (
                <RouterLink
                  key={i}
                  to={m.path}
                  data-testid={`qlp-int-${moduleKey}-${i}`}
                  className="flex items-start gap-2 p-1.5 rounded-md border border-indigo-100 bg-white hover:bg-indigo-50 hover:border-indigo-400 transition group"
                >
                  <div className="text-[11px] flex-1 min-w-0">
                    <div className="font-medium text-indigo-700 group-hover:text-indigo-900 flex items-center gap-1">
                      {m.label}
                      <ArrowUpRight className="w-2.5 h-2.5 opacity-60" />
                    </div>
                    {m.why && <div className="text-[10px] text-slate-500 truncate">{m.why}</div>}
                  </div>
                </RouterLink>
              ))}
              {internals.length === 0 && <span className="text-xs text-slate-400">No related modules listed.</span>}
            </div>
          </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default QuickLinksPanel;
